/**
 * #1114 (Ted 09-29, lane LA) — Settings → Agreements.
 *
 * Ted keeps three documents in Gingr (Happy Tail Happy Dog Agreement, Kennel
 * Crate Authorization Form, Pet Care Emergency Authorization) and wants
 * every client to have all three signed at the RESERVATION stage, with a
 * re-sign "when we make a substantive change". Gingr's template editor has
 * exactly two controls that matter and never got used — "Required for
 * Reservation Types" (empty on all three) and "Invalidate Signed Agreements"
 * (off) — which is why paperwork slips through. This page is those two
 * controls, per document, plus the two things Gingr never shows: who
 * published which version when, and who has signed what.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: one row per document leading with its title,
 *    then the version in force, what it is required for, and how many
 *    customers have signed it — each read off the same row the customer app
 *    reads. The engine key (`hthd-agreement`) is never shown raw.
 *  - #2 The next action is obvious: one primary "Add a document"; per row
 *    Edit (inline), History and Signatures (inline panels), and a two-tap
 *    Hide with the consequence spelled out. A non-owner sees the documents
 *    with a plain reason, never a dead control.
 *  - #5 Never lie about data: "Required for nothing" is named as such (and
 *    carries the warning rail, because such a document gates nobody);
 *    "Re-sign from v3" is shown only when the switch has ever been used; a
 *    hidden document stays listed, marked Hidden, so history reads true.
 *  - Five states: loading · error(+retry) · empty (no documents — teaching
 *    copy) · ready · saving (per-row / per-form in-flight lock).
 *
 * Counter Test: Ted fixes a typo in the crate form from his phone, leaves
 * the re-sign switch off, saves — nobody is bothered. Next month he adds a
 * liability clause, turns the switch ON, saves — every customer is asked to
 * sign that one document again before their next reservation, and the
 * History panel shows "v3 · Ted Gualtier · re-sign required".
 *
 * Server contract: GET/POST /api/agreements/admin/agreements,
 * PATCH/DELETE /agreements/:id, GET /agreements/:id/versions,
 * GET /agreements/signatures?agreementId=. Read contracts.customer:read:all;
 * write contracts.customer:write:all.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Checkbox, EmptyState, Input, Skeleton } from '@ferro/ui';
import {
  adminAgreementsApi,
  adminTenantServicesApi,
  type AgreementSignatureRow,
  type AgreementTemplate,
  type AgreementTemplatePatch,
  type AgreementVersionRow,
  type TenantServiceView,
} from '../../lib/api.js';
import './agreements-settings.css';

interface AgreementsSettingsCardProps {
  apiUrl: string;
  /** Holds contracts.customer:read:all (owner/manager/receptionist). */
  canRead: boolean;
  /** Holds contracts.customer:write:all (owner/manager). */
  canEdit: boolean;
}

type Status = 'loading' | 'error' | 'ready';

/** The wildcard `requiredFor` value — every service, including ones added later. */
export const REQUIRED_FOR_ALL = '*';
/** Reack ledger rows (packages/core/modules/agreements/reack-marker.ts) are not documents. */
const REACK_MARKER_PREFIX = 'reack-v';

export function isLedgerRow(a: Pick<AgreementTemplate, 'name'>): boolean {
  return a.name.startsWith(REACK_MARKER_PREFIX);
}

export interface DocumentForm {
  displayName: string;
  content: string;
  /** true ⇒ requiredFor ['*']. */
  everyService: boolean;
  services: string[];
  isActive: boolean;
  requireResign: boolean;
  note: string;
}

export function formFromTemplate(a: AgreementTemplate): DocumentForm {
  const every = a.requiredFor.includes(REQUIRED_FOR_ALL);
  return {
    displayName: a.displayName,
    content: a.content,
    everyService: every,
    services: every ? [] : a.requiredFor,
    isActive: a.isActive,
    requireResign: false,
    note: '',
  };
}

export const EMPTY_FORM: DocumentForm = {
  displayName: '',
  content: '',
  everyService: true,
  services: [],
  isActive: true,
  requireResign: false,
  note: '',
};

export function requiredForFromForm(f: DocumentForm): string[] {
  return f.everyService ? [REQUIRED_FOR_ALL] : [...new Set(f.services)];
}

/** The PATCH body for an edit: only what changed, plus the switch when it is on. */
export function patchFromForm(
  current: AgreementTemplate,
  f: DocumentForm,
): { ok: true; patch: AgreementTemplatePatch } | { ok: false; message: string } {
  const displayName = f.displayName.trim();
  if (displayName.length === 0 || displayName.length > 120) {
    return { ok: false, message: 'Give the document a title your customers will recognise (up to 120 characters).' };
  }
  if (f.content.trim().length === 0) return { ok: false, message: 'The document needs its text.' };
  const patch: AgreementTemplatePatch = {};
  if (displayName !== current.displayName) patch.displayName = displayName;
  if (f.content !== current.content) patch.content = f.content;
  const requiredFor = requiredForFromForm(f);
  if (JSON.stringify(requiredFor) !== JSON.stringify(current.requiredFor)) patch.requiredFor = requiredFor;
  if (f.isActive !== current.isActive) patch.isActive = f.isActive;
  if (f.requireResign) {
    patch.requireResign = true;
    // The switch publishes a version even with unchanged text; carry the
    // text so the history row holds exactly what customers will sign.
    if (patch.content === undefined) patch.content = f.content;
  }
  if (f.note.trim().length > 0) patch.note = f.note.trim().slice(0, 200);
  if (Object.keys(patch).length === 0 || (Object.keys(patch).length === 1 && patch.note !== undefined)) {
    return { ok: false, message: 'Nothing changed.' };
  }
  return { ok: true, patch };
}

/** A stable engine key from a title: "Kennel Crate Authorization Form" → "kennel-crate-authorization-form". */
export function slugFromTitle(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'unknown date';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function requiredForLabel(
  requiredFor: ReadonlyArray<string>,
  services: ReadonlyArray<Pick<TenantServiceView, 'name' | 'displayName'>>,
): { kind: 'all' | 'some' | 'none'; text: string } {
  if (requiredFor.includes(REQUIRED_FOR_ALL)) return { kind: 'all', text: 'Every service' };
  if (requiredFor.length === 0) return { kind: 'none', text: 'Required for nothing' };
  const names = requiredFor.map((n) => services.find((s) => s.name === n)?.displayName ?? n);
  return { kind: 'some', text: names.join(', ') };
}

export function AgreementsSettingsCard({ apiUrl, canRead, canEdit }: AgreementsSettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<AgreementTemplate[]>([]);
  const [services, setServices] = useState<TenantServiceView[]>([]);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<{ id: string; message: string } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DocumentForm>(EMPTY_FORM);
  const [confirmHideId, setConfirmHideId] = useState<string | null>(null);
  const [panel, setPanel] = useState<{ id: string; kind: 'history' | 'signatures' } | null>(null);
  const [history, setHistory] = useState<Record<string, AgreementVersionRow[]>>({});
  const [signatures, setSignatures] = useState<Record<string, AgreementSignatureRow[]>>({});
  const [panelError, setPanelError] = useState<string | null>(null);
  const [panelBusy, setPanelBusy] = useState(false);
  const [shownText, setShownText] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<DocumentForm>(EMPTY_FORM);
  const [addError, setAddError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const load = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial'): Promise<boolean> => {
      if (!canRead) {
        setStatus('ready');
        return true;
      }
      if (mode === 'initial') {
        setStatus('loading');
        setLoadError(null);
        setSaved(null);
      }
      const [list, svc] = await Promise.all([
        adminAgreementsApi.list(apiUrl),
        adminTenantServicesApi.list(apiUrl),
      ]);
      if (list.error || !list.data) {
        if (mode === 'initial') {
          setLoadError(list.error ?? 'Could not load your documents.');
          setStatus('error');
        }
        return false;
      }
      setTemplates(list.data.agreements);
      // The services read only feeds the required-for picker: a failure
      // names itself there instead of blocking the documents.
      if (svc.data) {
        setServices(svc.data.services);
        setServicesError(null);
      } else {
        setServicesError(svc.error ?? 'Could not load your services.');
      }
      setStatus('ready');
      return true;
    },
    [apiUrl, canRead],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const activeServices = useMemo(() => services.filter((s) => s.isActive), [services]);
  const documents = useMemo(() => templates.filter((t) => !isLedgerRow(t)), [templates]);
  const ledgerRows = useMemo(() => templates.filter(isLedgerRow), [templates]);

  const clearTransient = () => {
    setRowError(null);
    setSaved(null);
    setConfirmHideId(null);
  };

  const startEdit = (a: AgreementTemplate) => {
    clearTransient();
    setPanel(null);
    setEditingId(a.id);
    setForm(formFromTemplate(a));
  };

  const submitEdit = useCallback(
    async (a: AgreementTemplate) => {
      const parsed = patchFromForm(a, form);
      if (!parsed.ok) {
        setRowError({ id: a.id, message: parsed.message });
        return;
      }
      setBusyId(a.id);
      setRowError(null);
      setSaved(null);
      const res = await adminAgreementsApi.update(apiUrl, a.id, parsed.patch);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: a.id, message: res.error ?? 'Could not save that document.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setEditingId(null);
      const next = res.data.agreement;
      if (res.data.resignRequired) {
        setSaved(`Saved "${next.displayName}" as version ${next.version}. Re-sign required: every customer who signed an earlier version will be asked to sign this one before their next reservation.`);
      } else if (res.data.publishedNewVersion) {
        setSaved(`Saved "${next.displayName}" as version ${next.version}. Earlier signatures stay valid — nobody is asked to sign again.`);
      } else {
        setSaved(`Saved "${next.displayName}". The text did not change, so the version stays ${next.version}.`);
      }
    },
    [apiUrl, form, load],
  );

  const setActive = useCallback(
    async (a: AgreementTemplate, isActive: boolean) => {
      setBusyId(a.id);
      setRowError(null);
      setSaved(null);
      const res = isActive
        ? await adminAgreementsApi.update(apiUrl, a.id, { isActive: true })
        : await adminAgreementsApi.deactivate(apiUrl, a.id);
      setConfirmHideId(null);
      if (res.error || !res.data) {
        setBusyId(null);
        setRowError({ id: a.id, message: res.error ?? 'Could not change that document.' });
        return;
      }
      await load('refresh');
      setBusyId(null);
      setSaved(
        isActive
          ? `"${a.displayName}" is back in use. Customers who never signed it will be asked to.`
          : `"${a.displayName}" is hidden. Nobody is asked to sign it; existing signatures are kept.`,
      );
    },
    [apiUrl, load],
  );

  const openPanel = useCallback(
    async (a: AgreementTemplate, kind: 'history' | 'signatures') => {
      clearTransient();
      setEditingId(null);
      setShownText(null);
      if (panel?.id === a.id && panel.kind === kind) {
        setPanel(null);
        return;
      }
      setPanel({ id: a.id, kind });
      setPanelError(null);
      setPanelBusy(true);
      const res =
        kind === 'history'
          ? await adminAgreementsApi.versions(apiUrl, a.id)
          : await adminAgreementsApi.signatures(apiUrl, a.id);
      setPanelBusy(false);
      if (res.error || !res.data) {
        setPanelError(res.error ?? `Could not load the ${kind}.`);
        return;
      }
      if (kind === 'history') {
        setHistory((h) => ({ ...h, [a.id]: (res.data as { versions: AgreementVersionRow[] }).versions }));
      } else {
        setSignatures((s) => ({ ...s, [a.id]: (res.data as { signatures: AgreementSignatureRow[] }).signatures }));
      }
    },
    [apiUrl, panel],
  );

  const submitAdd = useCallback(async () => {
    const displayName = addForm.displayName.trim();
    if (displayName.length === 0 || displayName.length > 120) {
      setAddError('Give the document a title your customers will recognise (up to 120 characters).');
      return;
    }
    if (addForm.content.trim().length === 0) {
      setAddError('Paste the document text.');
      return;
    }
    const name = slugFromTitle(displayName);
    if (name.length === 0) {
      setAddError('The title needs at least one letter or number.');
      return;
    }
    if (templates.some((t) => t.name === name)) {
      setAddError(`A document with that title already exists ("${templates.find((t) => t.name === name)?.displayName ?? name}"). Edit it instead.`);
      return;
    }
    setIsAdding(true);
    setAddError(null);
    const res = await adminAgreementsApi.create(apiUrl, {
      name,
      displayName,
      content: addForm.content,
      requiredFor: requiredForFromForm(addForm),
    });
    setIsAdding(false);
    if (res.error || !res.data) {
      setAddError(res.error ?? 'Could not add that document.');
      return;
    }
    await load('refresh');
    setAddForm(EMPTY_FORM);
    setAddOpen(false);
    setSaved(`Added "${res.data.agreement.displayName}" (version 1). Customers will be asked to sign it before their next reservation.`);
  }, [addForm, apiUrl, load, templates]);

  // ── States ──────────────────────────────────────────────────────────

  if (!canRead) {
    return (
      <Card>
        <div className="ferro-settings__card" data-testid="agreements-no-access">
          <h2 className="ferro-settings__subheading">Agreements</h2>
          <p className="ferro-muted ferro-settings__footnote">
            Your role can't view customer agreements. An owner or manager manages them here.
          </p>
        </div>
      </Card>
    );
  }

  if (status === 'loading') {
    return (
      <Card>
        <div className="ferro-settings__card" data-testid="agreements-loading" aria-busy="true">
          <Skeleton variant="text" width="40%" />
          <Skeleton variant="rect" height="88px" />
          <Skeleton variant="rect" height="88px" />
          <Skeleton variant="rect" height="88px" />
        </div>
      </Card>
    );
  }

  if (status === 'error') {
    return (
      <Card>
        <div className="ferro-settings__card" data-testid="agreements-error">
          <Alert variant="error">{loadError ?? 'Could not load your documents.'}</Alert>
          <div className="ferro-settings__actions">
            <Button type="button" variant="primary" onClick={() => { void load(); }}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // ── Shared form fields (edit + add) ────────────────────────────────

  const renderFields = (
    f: DocumentForm,
    set: (next: (cur: DocumentForm) => DocumentForm) => void,
    idPrefix: string,
    opts: { showResign: boolean; showActive: boolean },
  ) => (
    <div className="ferro-settings__fieldset">
      <Input
        id={`${idPrefix}-title`}
        name="displayName"
        label="Title (what customers see)"
        type="text"
        value={f.displayName}
        onChange={(e) => set((cur) => ({ ...cur, displayName: e.target.value }))}
        placeholder="Kennel Crate Authorization Form"
        data-testid={`${idPrefix}-title`}
      />
      <div className="ferro-field">
        <label className="ferro-field__label" htmlFor={`${idPrefix}-content`}>
          Document text
        </label>
        <textarea
          id={`${idPrefix}-content`}
          name="content"
          className="ferro-input ferro-agreements__body"
          value={f.content}
          onChange={(e) => set((cur) => ({ ...cur, content: e.target.value }))}
          rows={12}
          placeholder="Paste the full text your customers must read and sign."
          data-testid={`${idPrefix}-content`}
        />
        <p className="ferro-muted ferro-settings__footnote">
          Plain text. A line starting with "## " shows as a section heading in the customer app.
        </p>
      </div>

      <fieldset className="ferro-settings__fieldset ferro-agreements__picker">
        <legend className="ferro-field__label">Required for</legend>
        <Checkbox
          id={`${idPrefix}-every`}
          name="everyService"
          label="Every service, including ones added later"
          description={'The same control as Gingr\u2019s "Required for Reservation Types", but it cannot be left empty by accident: a document required for nothing gates nobody.'}
          checked={f.everyService}
          onChange={(e) => set((cur) => ({ ...cur, everyService: e.target.checked }))}
          data-testid={`${idPrefix}-every`}
        />
        {!f.everyService ? (
          servicesError ? (
            <p className="ferro-muted ferro-settings__footnote" role="status">
              {servicesError} Choose "Every service" or try again.
            </p>
          ) : activeServices.length === 0 ? (
            <p className="ferro-muted ferro-settings__footnote">No services yet — add them under Services &amp; prices.</p>
          ) : (
            <div className="ferro-agreements__services">
              {activeServices.map((s) => (
                <Checkbox
                  key={s.id}
                  id={`${idPrefix}-svc-${s.name}`}
                  name={`service-${s.name}`}
                  label={s.displayName}
                  checked={f.services.includes(s.name)}
                  onChange={(e) =>
                    set((cur) => ({
                      ...cur,
                      services: e.target.checked
                        ? [...cur.services, s.name]
                        : cur.services.filter((n) => n !== s.name),
                    }))
                  }
                  data-testid={`${idPrefix}-svc-${s.name}`}
                />
              ))}
            </div>
          )
        ) : null}
        {!f.everyService && f.services.length === 0 && (
          <p className="ferro-agreements__warn" role="status">
            Required for nothing: no customer will be asked to sign this. Pick at least one service or "Every service".
          </p>
        )}
      </fieldset>

      {opts.showActive && (
        <Checkbox
          id={`${idPrefix}-active`}
          name="isActive"
          label="In use"
          description="Off hides the document: nobody is asked to sign it and existing signatures are kept."
          checked={f.isActive}
          onChange={(e) => set((cur) => ({ ...cur, isActive: e.target.checked }))}
          data-testid={`${idPrefix}-active`}
        />
      )}

      {opts.showResign && (
        <div className="ferro-agreements__resign">
          <Checkbox
            id={`${idPrefix}-resign`}
            name="requireResign"
            label="Require every customer to sign this version again"
            description="Turn this on for a substantive change (a new clause, a changed fee, a new liability). Customers who signed an earlier version must sign this one before their next reservation. Leave it off for typos and wording — their signatures stay valid and nobody is bothered."
            checked={f.requireResign}
            onChange={(e) => set((cur) => ({ ...cur, requireResign: e.target.checked }))}
            data-testid={`${idPrefix}-resign`}
          />
          <Input
            id={`${idPrefix}-note`}
            name="note"
            label="Note for the history (optional)"
            type="text"
            value={f.note}
            onChange={(e) => set((cur) => ({ ...cur, note: e.target.value }))}
            placeholder="Added the new late-pickup fee"
            maxLength={200}
            data-testid={`${idPrefix}-note`}
          />
        </div>
      )}
    </div>
  );

  // ── Ready ───────────────────────────────────────────────────────────

  return (
    <Card>
      <div className="ferro-settings__card" data-testid="agreements-ready">
        <div className="ferro-agreements__head">
          <div>
            <h2 className="ferro-settings__subheading">Agreements</h2>
            <p className="ferro-muted ferro-settings__footnote" data-testid="agreements-summary">
              {documents.length === 0
                ? 'The documents customers sign before a reservation.'
                : `${documents.filter((d) => d.isActive).length} in use · customers sign each one on its own, and sign again only when you say so.`}
            </p>
          </div>
          {canEdit && !addOpen && (
            <Button type="button" variant="primary" onClick={() => { clearTransient(); setEditingId(null); setAddOpen(true); }} data-testid="agreements-add">
              Add a document
            </Button>
          )}
        </div>

        {saved && (
          <div role="status" data-testid="agreements-saved">
            <Alert variant="success">{saved}</Alert>
          </div>
        )}

        {!canEdit && (
          <p className="ferro-muted ferro-settings__footnote" data-testid="agreements-readonly-reason">
            Owners and managers change these; you can read them and see who has signed.
          </p>
        )}

        {addOpen && canEdit && (
          <form
            className="ferro-form ferro-agreements__edit"
            data-testid="agreements-add-form"
            onSubmit={(e) => { e.preventDefault(); void submitAdd(); }}
          >
            <h3 className="ferro-settings__subheading">New document</h3>
            {renderFields(addForm, (fn) => setAddForm((cur) => fn(cur)), 'agreement-new', { showResign: false, showActive: false })}
            {addError && <Alert variant="error">{addError}</Alert>}
            <div className="ferro-settings__actions">
              <Button type="submit" variant="primary" isLoading={isAdding} data-testid="agreement-new-save">
                Add document
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setAddOpen(false); setAddError(null); }} disabled={isAdding}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {documents.length === 0 && !addOpen ? (
          <div data-testid="agreements-empty">
            <EmptyState
              title="No documents yet"
              description="Add the agreements your customers must sign — for example your terms and conditions, a crate authorization and an emergency veterinary authorization. Each one is signed on its own, and you decide when a change needs a new signature."
              {...(canEdit ? { action: { label: 'Add a document', onClick: () => setAddOpen(true) } } : {})}
            />
          </div>
        ) : null}

        {documents.length > 0 && (
          <ul className="ferro-agreements__list" data-testid="agreements-list">
            {documents.map((a) => {
              const req = requiredForLabel(a.requiredFor, services);
              const rail = !a.isActive ? 'neutral' : req.kind === 'none' ? 'warn' : 'ok';
              const isEditing = editingId === a.id;
              const isBusy = busyId === a.id;
              const thisPanel = panel?.id === a.id ? panel.kind : null;
              return (
                <li
                  key={a.id}
                  className={`ferro-agreements__row ferro-agreements__row--rail-${rail}${a.isActive ? '' : ' ferro-agreements__row--hidden'}`}
                  data-testid={`agreement-row-${a.id}`}
                >
                  <div className="ferro-agreements__main">
                    <div className="ferro-agreements__name" data-testid={`agreement-name-${a.id}`}>{a.displayName}</div>
                    <div className="ferro-agreements__facts">
                      <Badge variant={a.isActive ? 'active' : 'pending'}>{a.isActive ? 'In use' : 'Hidden'}</Badge>
                      <span data-testid={`agreement-version-${a.id}`}>Version {a.version}</span>
                      {a.resignRequiredFrom > 1 && (
                        <span className="ferro-muted" data-testid={`agreement-resign-${a.id}`}>
                          Re-sign required from v{a.resignRequiredFrom}
                        </span>
                      )}
                      <span
                        className={`ferro-agreements__chip ferro-agreements__chip--${req.kind}`}
                        data-testid={`agreement-required-${a.id}`}
                      >
                        {req.kind === 'none' ? req.text : `Required for: ${req.text}`}
                      </span>
                    </div>
                  </div>

                  <div className="ferro-agreements__actions">
                    <Button type="button" variant="ghost" size="sm" onClick={() => { void openPanel(a, 'history'); }} aria-expanded={thisPanel === 'history'} data-testid={`agreement-history-${a.id}`}>
                      History
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => { void openPanel(a, 'signatures'); }} aria-expanded={thisPanel === 'signatures'} data-testid={`agreement-signatures-${a.id}`}>
                      Signatures
                    </Button>
                    {canEdit && !isEditing && (
                      <Button type="button" variant="outline" size="sm" onClick={() => startEdit(a)} disabled={isBusy} data-testid={`agreement-edit-${a.id}`}>
                        Edit
                      </Button>
                    )}
                    {canEdit && a.isActive && !isEditing && (
                      confirmHideId === a.id ? (
                        <>
                          <Button type="button" variant="danger" size="sm" isLoading={isBusy} onClick={() => { void setActive(a, false); }} data-testid={`agreement-hide-confirm-${a.id}`}>
                            Yes, hide it
                          </Button>
                          <Button type="button" variant="ghost" size="sm" onClick={() => setConfirmHideId(null)} disabled={isBusy}>
                            Keep
                          </Button>
                        </>
                      ) : (
                        <Button type="button" variant="ghost" size="sm" onClick={() => { clearTransient(); setConfirmHideId(a.id); }} disabled={isBusy} data-testid={`agreement-hide-${a.id}`}>
                          Hide
                        </Button>
                      )
                    )}
                    {canEdit && !a.isActive && !isEditing && (
                      <Button type="button" variant="outline" size="sm" isLoading={isBusy} onClick={() => { void setActive(a, true); }} data-testid={`agreement-unhide-${a.id}`}>
                        Put back in use
                      </Button>
                    )}
                  </div>

                  {confirmHideId === a.id && (
                    <p className="ferro-muted ferro-agreements__note" role="status">
                      Hiding stops asking customers to sign "{a.displayName}". Signatures already recorded are kept, and you can put it back in use later.
                    </p>
                  )}

                  {rowError?.id === a.id && (
                    <div className="ferro-agreements__error" role="alert" data-testid={`agreement-error-${a.id}`}>
                      <Alert variant="error">{rowError.message}</Alert>
                    </div>
                  )}

                  {isEditing && canEdit && (
                    <form
                      className="ferro-form ferro-agreements__edit"
                      data-testid={`agreement-edit-form-${a.id}`}
                      onSubmit={(e) => { e.preventDefault(); void submitEdit(a); }}
                    >
                      {renderFields(form, (fn) => setForm((cur) => fn(cur)), `agreement-${a.id}`, { showResign: true, showActive: true })}
                      <div className="ferro-settings__actions">
                        <Button type="submit" variant="primary" isLoading={isBusy} data-testid={`agreement-save-${a.id}`}>
                          {form.requireResign ? 'Save and require re-sign' : 'Save'}
                        </Button>
                        <Button type="button" variant="ghost" onClick={() => { setEditingId(null); setRowError(null); }} disabled={isBusy}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  )}

                  {thisPanel === 'history' && (
                    <div className="ferro-agreements__panel" data-testid={`agreement-history-panel-${a.id}`}>
                      <h4 className="ferro-agreements__panel-title">Version history</h4>
                      {panelBusy ? (
                        <Skeleton variant="rect" height="48px" />
                      ) : panelError ? (
                        <Alert variant="error">{panelError}</Alert>
                      ) : (history[a.id] ?? []).length === 0 ? (
                        <p className="ferro-muted ferro-settings__footnote">No history recorded yet — versions published from here will appear with who and when.</p>
                      ) : (
                        <ol className="ferro-agreements__history">
                          {(history[a.id] ?? []).map((v) => (
                            <li key={v.id} className="ferro-agreements__history-row" data-testid={`agreement-history-row-${a.id}-${v.version}`}>
                              <div className="ferro-agreements__history-main">
                                <strong>v{v.version}</strong>
                                <span className="ferro-muted">{formatWhen(v.createdAt)}</span>
                                <span>{v.changedByName ?? (v.note === 'seed' || v.note === 'backfill' ? 'Set up by the platform' : 'Unknown editor')}</span>
                                {v.requireResign ? <Badge variant="warning">Re-sign required</Badge> : <Badge variant="info">Signatures kept</Badge>}
                                {v.note && v.note !== 'seed' && v.note !== 'backfill' && v.note !== 'created' ? (
                                  <span className="ferro-muted">“{v.note}”</span>
                                ) : null}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setShownText((cur) => (cur === v.id ? null : v.id))}
                                aria-expanded={shownText === v.id}
                              >
                                {shownText === v.id ? 'Hide text' : 'Show text'}
                              </Button>
                              {shownText === v.id && (
                                <pre className="ferro-agreements__text" data-testid={`agreement-history-text-${a.id}-${v.version}`}>{v.content}</pre>
                              )}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}

                  {thisPanel === 'signatures' && (
                    <div className="ferro-agreements__panel" data-testid={`agreement-signatures-panel-${a.id}`}>
                      <h4 className="ferro-agreements__panel-title">Who has signed</h4>
                      {panelBusy ? (
                        <Skeleton variant="rect" height="48px" />
                      ) : panelError ? (
                        <Alert variant="error">{panelError}</Alert>
                      ) : (signatures[a.id] ?? []).length === 0 ? (
                        <p className="ferro-muted ferro-settings__footnote">Nobody has signed this document yet.</p>
                      ) : (
                        <ul className="ferro-agreements__sigs">
                          {(signatures[a.id] ?? []).map((s) => {
                            const current = s.signedVersion >= a.resignRequiredFrom;
                            const who = s.customer ? `${s.customer.firstName} ${s.customer.lastName}`.trim() : s.typedName;
                            return (
                              <li key={s.id} className="ferro-agreements__sig-row" data-testid={`agreement-signature-row-${s.id}`}>
                                <div className="ferro-agreements__sig-main">
                                  <strong>{who || s.typedName}</strong>
                                  {s.customer?.email ? <span className="ferro-muted">{s.customer.email}</span> : null}
                                </div>
                                <div className="ferro-agreements__sig-facts">
                                  <span>Signed v{s.signedVersion} · {formatWhen(s.signedAt)}</span>
                                  <Badge variant={current ? 'success' : 'warning'}>{current ? 'Current' : 'Must sign again'}</Badge>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {ledgerRows.length > 0 && (
          <p className="ferro-muted ferro-settings__footnote" data-testid="agreements-ledger-note">
            {ledgerRows.length === 1 ? 'One acknowledgment ledger row' : `${ledgerRows.length} acknowledgment ledger rows`} ({ledgerRows.map((r) => r.name).join(', ')}) {ledgerRows.length === 1 ? 'is' : 'are'} kept for the audit trail. {ledgerRows.some((r) => r.isActive) ? 'They are not documents and customers never see them.' : ''}
          </p>
        )}
      </div>
    </Card>
  );
}
